import React, { useEffect, useState, useCallback, useRef } from 'react';
import { CORPORATE_RE, isHonorificToken, honorificOptions } from '../../utils/statutory';
import { fmtDateTime } from '../../utils/region';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Pagination, { getPageSize } from '../Pagination';
import EditClient from '../EditClient';
import ExternalPros, { ProForm } from '../ExternalPros';
import TelegramBind from '../Tax/TelegramBind';
import BillMonthDialog from './BillMonth';
import Fab from '../Fab';
import { api, apiFetch, isApiDownResponse, apiDownError } from '../../utils/api';
import { normalisePersonNamePart, fixAllCaps } from '../../utils/personName';
import { companyNameGap, namePartsKey, personNameGap } from '../../utils/clientNameGap';
import { displayClientName, displayCompanyName } from '../../utils/displayName';
import { rowTint, lighten, tabColor, menuPill, pageLine, EMAIL_CAL_GROUP, TAB_GROUPS, pageToolbar, PAGE_INSET } from '../../tabPalette';
import { tint } from '../../utils/accountColors';
import { brandOr, firmLabel, firmLabelOr } from '../../utils/brand';
import { useDialDefaults } from '../../utils/dialcode';
import { labelKinds } from '../../utils/firmKind';

// ── Types ───────────────────────────────────────────────────────────────────
interface Contact {
  id: string;
  user_id: string;
  visibility: 'private' | 'public' | 'office';
  name: string;
  name_first?: string;    // an individual's First + Last, for the name the list shows (report 101b5c69)
  name_last?: string;
  email: string;
  phone: string;
  company: string;
  notes: string;
  source: 'manual' | 'email' | 'ocr';
  source_label: string;   // the firm label(s) of this contact (utils/brand firmLabel), comma-joined, or ''
  entity_type?: string;   // 'company' (juristic person) | 'individual' (natural person)
  tax_id?: string;
  branch_no?: string;
  branch_head?: number;
  branch?: string;         // the firm's branch the card is placed in, '' = none (report 7be30766) — not branch_no
  address?: string;
  client_id?: string;      // linked unified client record (empty ⇒ plain contact)
  cl_type?: string;        // linked client's accounting type (TRADING/PROPERTY/INACTIVE)
  cl_status?: string;      // linked client's acct_status JSON (['monthly','tax','audit'])
  svc_count?: number;      // number of configured service items on the linked client
  has_bs?: number;         // 1 ⇒ linked client has a Balance Sheet (a real client of ours)
  active?: number;         // effective active/inactive (1/0); inactive ⇒ greyed + sorted last
  own_active?: number | null;  // the contact's OWN flag (null = nobody has said). Only consulted
                               // when there is no client — clients.active stays the single truth
                               // for anyone who has one (owner 2026-09-19).
  active_source?: string;      // 'client' | 'contact' | 'derived' | 'unset' — where `active` came from
  tax_services?: string[]; // which tax services the client has: vat/wht/sso/income_tax/property_tax
  created_by_name: string;
  created_at: number;
  updated_by_name?: string;
  updated_at?: number;
}

// Tax-service chips — which filings each Accounting client is signed up for.
const TAX_CHIP: Record<string, { label: string; bg: string; color: string; title: string }> = {
  vat:          { label: 'VAT',  bg: '#DBEAFE', color: '#1D4ED8', title: 'VAT (PP30)' },
  wht:          { label: 'WHT',  bg: '#EDE9FE', color: '#6D28D9', title: 'Withholding Tax' },
  sso:          { label: 'SSO',  bg: '#D1FAE5', color: '#047857', title: 'Social Security' },
  income_tax:   { label: 'PND1', bg: '#FEF3C7', color: '#B45309', title: 'Income Tax (PND1)' },
  property_tax: { label: 'Prop', bg: '#FCE7E7', color: '#B91C1C', title: 'Property Tax' },
};

// Linked-client chip colours (mirror the Accounting module).
const CL_TYPE_COLORS: Record<string, { bg: string; color: string }> = {
  TRADING:  { bg: '#ede9fe', color: '#6d28d9' },
  PROPERTY: { bg: '#ffedd5', color: '#c2410c' },
  INACTIVE: { bg: '#f3f4f6', color: '#6b7280' },
};
const CL_STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  monthly: { bg: '#dbeafe', color: '#1d4ed8' },
  yearly:  { bg: '#ffedd5', color: '#c2410c' },
  tax:     { bg: '#dcfce7', color: '#15803d' },
  audit:   { bg: '#fef9c3', color: '#a16207' },
};
function parseClStatus(s?: string): string[] { try { const a = JSON.parse(s || '[]'); return Array.isArray(a) ? a.map(String) : []; } catch { return []; } }

// A row that isn't inactive is the top menu's Email / Contacts / Calendar purple — the box's own wash laid on white,
// #F3F2FC (rowTint at no shades) — not transparent, a Monthly or Yearly client's as well, whose green or amber is in its
// M / Y bubbles (see PlanBadges). An inactive row keeps its grey. It was that wash two shades lighter, #F9F9FD (Calem,
// Bugify #1a85b4b0 — see rowTint), all but white on the page, until Calem, report 74d77066 ("Make the bubble background
// two shades darker"): two shades back up lighten's scale, the step the bank cards took (report ca3f6cf6, three → one).
const PLAIN_ROW_BG = rowTint('contact', 0)!;
// The fill of the M bubble (green) and the Y bubble (amber), PlanBadges. Calem, report 21224c07, 2nd opening: "Remove the
// yellow and green background from the general bubble (to purple) and put it into the M and Y bubble instead. Make the
// yellow and green backgrounds two shades darker as well". The whole card wore them until then, #F3FCF9 / #FEF9F0 —
// rgba(16,185,129,0.05) and rgba(245,158,11,0.06) laid on white, made faint for Calem, Bugify #9ad7e6c7 ("make the
// background color faint/light"), where report 13ecfa12 had taken away 0.07 / 0.09. These are rgba(16,185,129,0.10)
// and rgba(245,158,11,0.12) laid on white: two shades darker counted as rowTint counts two shades lighter (half-way to
// white, the step these washes took for "two shades lighter", Bugify #1b192804), so two shades lighter of each is
// exactly the colour the card had. Not darken's step for lines, a quarter of the way on to the solid colour a shade,
// which would have taken them half-way to the solid green and amber. Solid, so they look the same whatever they sit on.
const MONTHLY_BG = '#E7F8F2';
const YEARLY_BG = '#FEF3E2';

// How a row reads at a glance — shared by the full-width list and the columns of the All view.
function rowLook(contact: Contact) {
  const isAcct = labelKinds(contact.source_label).accounting;
  // Greyed + already sorted last by the API. An EXPLICIT setting always shows (owner 2026-09-19 made
  // the status editable, and a switch you cannot see the effect of is not a switch); a merely DERIVED
  // one stays behind the Accounting gate, because 1062 natural persons would otherwise go grey
  // overnight on the strength of "has no balance sheet yet".
  const explicitStatus = contact.active_source === 'client' || contact.active_source === 'contact';
  const isInactive = contact.active === 0 && (isAcct || explicitStatus);
  const clStatuses = parseClStatus(contact.cl_status);
  const isMonthly = isAcct && clStatuses.includes('monthly'); // monthly clients = the green M bubble (PlanBadges)
  const isYearly = isAcct && clStatuses.includes('yearly');   // yearly clients = the amber Y bubble (both if both)
  // Row background, inside the card's purple edge: inactive grey; every other row the plain purple, a Monthly or Yearly
  // client's too (Calem, report 21224c07, 2nd opening: "Remove the yellow and green background from the general bubble
  // (to purple)"), its green / amber now in the M / Y bubbles (MONTHLY_BG / YEARLY_BG). Such a card was faint green or
  // amber all over, and one on both plans left green / right amber, split at the middle. The grey is --c-gray-50 written
  // out, #F7F6F3, so a hover can take it three shades darker as it does the purple (see cardStyle): lighten reads a
  // colour, not a CSS variable.
  const rowBg = isInactive ? '#F7F6F3' : PLAIN_ROW_BG;
  return { isAcct, isInactive, isMonthly, isYearly, rowBg };
}

// Each contact is its own card (Calem, report cba01676: "Make each contact its own bubble with the same color profile as
// the top menu"), built the way the Jobs, Bank and Quotation lists build theirs (cardLine there). The rows were flat, a
// thin grey line under each. The top menu's Email / Contacts / Calendar box made a card: its line is the card's edge, 1px
// as on the box, with their 10px corners, and the fill is still the row's own (rowLook: that box's purple wash — two
// shades lighter until report 74d77066 — a Monthly / Yearly client's as well, or inactive grey). The edge is one shade
// darker than the box's line since Calem, report 13ecfa12 ("Make the
// purple border around the bubbles in the detail one shade darker"): EMAIL_CAL_GROUP.border's 0.33 → 0.50, a quarter of
// the way to the solid #7F77DD, the step the Calendar's Week / Day cards took (cardLine there); the title bars above the
// All view's columns keep the box's own line. Then a shade darker again (Calem, report ddf1ba0e, 3rd opening: "darken
// up the bubble border on each page by one shade"): 0.50 → 0.625, a quarter of the way on again, where the Calendar's
// cards were until that same opening, so on white it goes #BFBBEE → #AFAAEA. Then again (Calem, report 0fb815da: "Make
// the bubble border one shade darker for everything on the purple menu pages (email/contacts/calendar)"): 0.625 → 0.72,
// a quarter of the way on once more (0.71875, to two places as the other pages' lines are — see darken), where the
// Calendar's cards were until that report, #A39DE7 on white; the M / Y bubbles with them. The title bars, their count
// chips and the toolbar's unselected tabs took the box's line a shade darker then (pageLine). The cards sit on the
// page's white now (see the page). A Monthly / Yearly client is marked by the green / amber M / Y bubbles at the card's
// right-hand end (PlanBadges) — not by its fill, faint green / amber until report 21224c07's 2nd opening, nor by a
// bar, since Calem, report f6527515: "Remove the
// extra thickness on the left-hand side of each bubble". A 3px bar in the full green / amber ran down the card's left, an
// inset shadow inside the edge. Cards stand CARD_GAP apart — their top and bottom margins; the two between neighbours
// fold into one gap — 7px where it was 4 (Calem, report 5d3e1bce: "On all system pages (except tracking) increase the
// gaps between the bubbles by 3 pixels"),
// and run the full width of the list or column, as wide as the title bar above them (Calem,
// cba01676, 2nd opening: "Increase the bubble size to stretch out to the menu width"); they stood 6px in from either
// side. Hover takes the card's fill three shades darker, an inactive card's grey too, and gives it back on leaving
// (Calem, report 28e90b5f, 2nd opening: "Instead of two shades lighter make it three shades darker"): down lighten's own
// scale, away from white, so the purple goes #F3F2FC → #EAE8FA (#F9F9FD → #F5F5FC while the fill was two shades
// lighter, before report 74d77066) and the grey #F7F6F3 → #F1EFEA. Not darken, whose shade
// is a quarter of the way to black and would make the card charcoal. It was two shades lighter (the 1st opening: "On
// all system pages when you hover over a bubble, instead of making the border thicker, make the bubble background two
// shades lighter"). Hover had kept the fill and ringed the card in the box's solid purple, EMAIL_CAL_GROUP.color, 1px
// inside its edge (an inset shadow), which read as a thicker edge; it turned the row grey before that. Shared by the
// full-width list and the columns of the All view.
const CARD_R = 10;
const CARD_GAP = 7;
const CARD_LINE = tint(EMAIL_CAL_GROUP.color, 0.72);
function cardStyle(look: { rowBg: string; isInactive: boolean } | undefined, hovered: boolean): React.CSSProperties {
  const fill = look?.rowBg || PLAIN_ROW_BG;
  return {
    margin: `${CARD_GAP}px 0`,
    background: hovered ? lighten(fill, -3) : fill,
    border: `1px solid ${CARD_LINE}`, borderRadius: CARD_R,
    opacity: look?.isInactive ? 0.55 : 1,
  };
}

// Monthly / Yearly — an M bubble for a monthly client, a Y bubble for a yearly one, both for a client on both plans (M
// first, 4px apart — CARD_GAP until the cards moved apart in report 5d3e1bce, which left these where they were). Calem,
// Bugify #9ad7e6c7: "Change the monthly and yearly circles into bubbles at the end of
// the row". They sit at the card's right-hand end (marginLeft auto), on the full-width list and in the All view's
// columns alike: after the name and email, before the hover actions, and a long name gives way to them. They were 16px
// circles straight after the name, ringed in blue on the blue wash (report 13ecfa12). Each is now a circle of its own,
// 22px across (Bugify #836d4443: "Change these to circles"), where #9ad7e6c7 made them 26 × 22 cards with the 8px
// --radius-md corners of the action buttons beside them. They have the cards' purple edge (CARD_LINE), and
// the letter in the top menu's Quotation / Invoice / Receipt blue, TAB_GROUPS.peak.color — the blue Calem asked for in
// 13ecfa12's 2nd opening ("change them to blue color (accounting menu)"). The M is filled green and the Y amber
// (MONTHLY_BG / YEARLY_BG), the colours the whole card had, two shades darker (Calem, report 21224c07, 2nd opening);
// both were white. The blue letter still reads on them: 3.3:1 against either fill, where it was 3.6:1 against the
// white. The letter's own span is trimmed to its cap height and baseline (text-box-trim, as the top menu's red count
// bubbles are — see Portal's badgeSpan), so the flex box centres the capital itself, across and down (Bugify
// #836d4443, 2nd opening: "Make sure the M and Y are centered vertically and horizontally inside the circle"). The
// flex box centred DM Sans's line box, one em tall, which keeps room above the capital for accents and below it for
// descenders — not the same amount, and each rounded to whole pixels. A browser without text-box-trim ignores it and
// centres the line box as before. Size, colours and place are as they were. They show on the card of an Accounting
// client marked Monthly or Yearly that isn't inactive (an inactive card stays plain grey). The tooltip names the plan.
function PlanBadges({ look }: { look: { isMonthly: boolean; isYearly: boolean; isInactive: boolean } }) {
  const { t } = useTranslation();
  if (look.isInactive || !(look.isMonthly || look.isYearly)) return null;
  const bubble = (fill: string): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxSizing: 'border-box',
    width: 22, height: 22, borderRadius: '50%', border: `1px solid ${CARD_LINE}`, background: fill,
    color: TAB_GROUPS.peak.color, fontSize: 12, fontWeight: 800, lineHeight: 1,
  });
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0, marginLeft: 'auto' }}>
      {look.isMonthly && <span title={t('ct_monthly_tip')} style={bubble(MONTHLY_BG)}><span style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic' }}>M</span></span>}
      {look.isYearly && <span title={t('ct_yearly_tip')} style={bubble(YEARLY_BG)}><span style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic' }}>Y</span></span>}
    </span>
  );
}

// The /contacts filter behind each category — its full-width tab and its column in All use the same.
function setCategoryFilter(params: URLSearchParams, tab: string) {
  if (tab === 'legal') params.set('source_label', firmLabel('legal'));
  else if (tab === 'accounting') params.set('source_label', firmLabel('accounting'));
  else if (tab !== 'all') params.set('visibility', tab);
}

// ── API helpers ──────────────────────────────────────────────────────────────
const BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
function getToken() { return localStorage.getItem('mf_token') || ''; }
async function apiReq(method: string, path: string, body?: any) {
  // apiFetch: a restart's failure brings up the "updating" notice and reads as such (sent once, as before).
  const res = await apiFetch(`${BASE}${path}`, {
    method,
    headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (isApiDownResponse(res)) throw apiDownError();
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Error'); }
  return res.json();
}

// ── Shared styles ────────────────────────────────────────────────────────────
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 12px', fontSize: 13,
  borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)',
  background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none',
};
const labelStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)',
  display: 'block', marginBottom: 4,
};
const btnStyle = (bg: string, color = '#fff'): React.CSSProperties => ({
  padding: '8px 14px', borderRadius: 'var(--radius-md)', border: 'none',
  background: bg, color, fontSize: 13, fontWeight: 600, cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 6,
});

// ── Helpers ──────────────────────────────────────────────────────────────────
function getInitials(name: string): string {
  const trimmed = (name || '').trim();
  if (!trimmed) return '@';
  return trimmed.charAt(0).toUpperCase();
}

// ── Toast ────────────────────────────────────────────────────────────────────
function Toast({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 4000);
    return () => clearTimeout(t);
  }, [onDismiss]);

  return (
    <div style={{
      position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
      background: 'var(--text-primary)', color: '#fff', padding: '10px 18px',
      borderRadius: 'var(--radius-lg)', fontSize: 13, fontWeight: 500,
      boxShadow: 'var(--shadow-md)', zIndex: 2000, display: 'flex',
      alignItems: 'center', gap: 10, animation: 'slideUp 0.2s ease',
      whiteSpace: 'nowrap',
    }}>
      <i className="ti ti-circle-check" style={{ fontSize: 16, color: 'var(--accent)' }} aria-hidden />
      {message}
      <button onClick={onDismiss} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', padding: 0, display: 'flex' }}>
        <i className="ti ti-x" style={{ fontSize: 14 }} aria-hidden />
      </button>
    </div>
  );
}

// ── Contact Form Modal ───────────────────────────────────────────────────────
interface ContactFormProps {
  contact: Contact | null;
  // A NEW card that opens already holding these — PandaAI Email's "Add to contacts" bubble passes the
  // sender's name and address. Ignored when editing an existing contact.
  initial?: { name?: string; email?: string };
  onClose: () => void;
  onSaved: () => void;
}

export function ContactForm({ contact, initial, onClose, onSaved }: ContactFormProps) {
  const { t } = useTranslation();
  const isNew = !contact;
  const seed = isNew ? initial : undefined;

  const [name, setName] = useState(contact?.name || seed?.name || '');
  // Structured person name (owner 2026-09-16, same shape as clients): shown instead of the single
  // Name field for INDIVIDUALS. `name` stays the canonical display string, assembled on save.
  // Legacy contacts without parts prefill by best guess — the human at the form is the judge —
  // except a corporate-looking string, which must not be guessed into person parts.
  const partsOf = (c: any) => {
    if (c?.name_first || c?.name_last || c?.name_prefix || c?.name_middle) {
      return { prefix: c.name_prefix || '', first: c.name_first || '', middle: c.name_middle || '', last: c.name_last || '' };
    }
    const cn = String(c?.name || '').trim();
    if (CORPORATE_RE.test(cn)) {
      return { prefix: '', first: '', middle: '', last: '' };
    }
    const toks = cn.split(/\s+/).filter(Boolean);
    const pre = toks.length && isHonorificToken(toks[0]) ? toks.shift()! : '';
    return { prefix: pre, first: toks[0] || '', middle: toks.slice(1, -1).join(' '), last: toks.length > 1 ? toks[toks.length - 1] : '' };
  };
  const raw0 = partsOf(contact || seed);
  // A person's card opens with any name word saved in capitals corrected — "JUN" / "LIU" show as
  // "Jun" / "Liu", and Save keeps it (Calem, report 077fd777). Only words written wholly in capitals
  // move: "McDonald", Thai and the workspace's own capitals word stay as saved, and so does the Prefix.
  const opensAsPerson = contact?.entity_type === 'individual' || !!(seed && raw0.first);
  const p0 = opensAsPerson ? { ...raw0, first: fixAllCaps(raw0.first), middle: fixAllCaps(raw0.middle), last: fixAllCaps(raw0.last) } : raw0;
  const [namePrefix, setNamePrefix] = useState(p0.prefix);
  const [nameFirst, setNameFirst] = useState(p0.first);
  const [nameMiddle, setNameMiddle] = useState(p0.middle);
  const [nameLast, setNameLast] = useState(p0.last);
  const assembledName = [namePrefix, nameFirst, nameMiddle, nameLast].map(v => v.trim()).filter(Boolean).join(' ');
  const [email, setEmail] = useState(contact?.email || seed?.email || '');
  const [phone, setPhone] = useState(contact?.phone || '');
  const { phoneHint } = useDialDefaults();   // the active company's dial code (+ a sample when its country pack has one)
  const [company, setCompany] = useState(contact?.company || '');
  // Company-type contact: Name IS the company (firm convention, 2026-07-28 "A") — the two fields
  // mirror each other live so a rename visibly carries into the invoice-header company name.
  const setNameSynced = (v: string) => { setName(v); if (entityType === 'company') setCompany(v); };
  const setCompanySynced = (v: string) => { setCompany(v); if (entityType === 'company') setName(v); };
  // Individual (natural person) vs company (juristic person). A NEW card starts with neither picked
  // ('') and is not saved until the operator picks one — starting on Company filed a person's typed
  // name as the "Company name" (report 0d95cfed); no type is guessed (services/entityIdentity.ts).
  // A name handed in from an email still fills whichever is picked: the Company name box as given,
  // the person parts when it reads as a person's. An existing card opens on its stored type.
  const [entityType, setEntityType] = useState<'individual' | 'company' | ''>(isNew ? '' : (opensAsPerson ? 'individual' : 'company'));
  const [taxId, setTaxId] = useState(contact?.tax_id || '');
  const [branchNo, setBranchNo] = useState(contact?.branch_no || '');
  const [address, setAddress] = useState(contact?.address || '');
  // Three states, and "not set" is one of them: 854 natural persons have never been triaged, and
  // writing 0 for them on the first Save would claim something untrue (owner 2026-09-19).
  const [status, setStatus] = useState<'unset' | 'active' | 'inactive'>(() => {
    if (!contact) return 'unset';
    if (contact.client_id) return contact.active_source === 'client' ? (contact.active ? 'active' : 'inactive') : 'unset';
    return contact.own_active == null ? 'unset' : (contact.own_active ? 'active' : 'inactive');
  });
  const [notes, setNotes] = useState(contact?.notes || '');
  // Firm membership (a client can be BOTH Legal and Accounting) + visibility. The firm tag(s) are the
  // single place this is set: stored as source_label ('SLF Legal' / 'SLF Accounting' / both comma-joined)
  // and mirrored onto the linked client's `firms`. Office/Private only matters for non-firm contacts.
  const initFirms: ('legal' | 'accounting')[] = [];
  if (firmLabel('legal') && (contact?.source_label || '').includes(firmLabel('legal'))) initFirms.push('legal');
  if (firmLabel('accounting') && (contact?.source_label || '').includes(firmLabel('accounting'))) initFirms.push('accounting');
  const [firms, setFirms] = useState<('legal' | 'accounting')[]>(initFirms);
  // '' = no choice yet. Owner 2026-09-16: where a contact belongs is REQUIRED and defaults to
  // NOTHING — a firm tick or an explicit Office/Private, but never a silent fall-through.
  // Private is retired (owner 2026-09-16): a non-firm contact is Office, chosen explicitly.
  const [vis, setVis] = useState<'office' | ''>(contact ? 'office' : '');
  const toggleFirm = (f: 'legal' | 'accounting') => setFirms(prev => prev.includes(f) ? prev.filter(x => x !== f) : [...prev, f]);
  // The branch the card is placed in, staff and clients alike (Calem, report 7be30766): one of the list kept in Admin →
  // Contacts, which the server hands out in alphabetical order. A branch since taken off that list stays offered on the
  // card that has it, so opening that card and saving keeps it.
  const [branch, setBranch] = useState(contact?.branch || '');
  const [branchList, setBranchList] = useState<string[]>([]);
  useEffect(() => {
    apiReq('GET', '/contacts/branches').then((d: any) => setBranchList(Array.isArray(d?.branches) ? d.branches : [])).catch(() => { });
  }, []);
  const branchOpts = branch && !branchList.includes(branch)
    ? [...branchList, branch].sort((a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' }))
    : branchList;
  // Marketing owners — who SOURCED this client (multi + %); the firm's office holds the remainder. Only
  // for an existing firm client (needs a linked client_id). A new contact defaults to office 100%.
  const [mktOwners, setMktOwners] = useState<{ userId: string; percent: number }[]>([]);
  const [mktOffices, setMktOffices] = useState<{ userId: string; name: string; email: string }[]>([]);
  const [staffUsers, setStaffUsers] = useState<{ id: string; name: string; email: string }[]>([]);
  const mktSum = mktOwners.reduce((s, o) => s + (Number(o.percent) || 0), 0);
  useEffect(() => {
    // Staff list serves the picker on NEW cards too (owner 2026-09-16); owners/offices only exist
    // once the card is a client.
    api.getServiceUsers().then((d: any) => setStaffUsers(Array.isArray(d) ? d : (d?.rows || []))).catch(() => { });
    if (!contact?.client_id) return;
    api.getMarketingOwners(contact.client_id).then((d: any) => { setMktOwners((d.owners || []).map((o: any) => ({ userId: o.userId, percent: o.percent }))); setMktOffices(d.offices || []); }).catch(() => { });
  }, [contact?.client_id]);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [editClientId, setEditClientId] = useState<string | false>(false);
  const [linking, setLinking] = useState(false);
  // Live duplicate-name warning (owner 2026-09-16): a card under an SLF tag IS the client, so a
  // same-name card is a same-name client. Warned as you type; the save's 409 is the hard gate.
  const [dupWarn, setDupWarn] = useState('');
  const checkDup = async (nm: string) => {
    const q = nm.trim();
    if (!q) { setDupWarn(''); return; }
    try {
      const r: any = await apiReq('GET', `/contacts/name-check?name=${encodeURIComponent(q)}&excludeId=${contact?.id || ''}`);
      setDupWarn(r?.duplicate ? t('ct_dup_name', { name: r.duplicate.name }) : '');
    } catch { /* the save-side gate still holds */ }
  };
  // After creating a firm COMPANY, the flow continues straight into its client record
  // (owner 2026-09-16: these lived only on existing cards; creation needs them too).
  const [openedAfterCreate, setOpenedAfterCreate] = useState(false);
  // Set once the POST has succeeded, so a retry updates that row instead of creating a twin.
  const [createdId, setCreatedId] = useState('');

  // Promote a tagged contact into a full client record, then open it.
  const openOrCreateClient = async () => {
    if (contact?.client_id) { setEditClientId(contact.client_id); return; }
    if (!contact) return;
    setLinking(true); setErr('');
    try { const r = await api.createClientFromContact(contact.id); setEditClientId(r.id); }
    catch (e: any) { setErr(e.message); } finally { setLinking(false); }
  };

  const doSave = async (): Promise<any | null> => {
    setSaving(true); setErr('');
    if (mktSum > 100.001) { setErr(t('ct_mkt_over')); setSaving(false); return null; }
    // Company or Individual is the operator's pick, not a default (report 0d95cfed).
    if (!entityType) { setErr(t('ct_need_entity')); setSaving(false); return null; }
    // Belonging is a decision, not a default (owner 2026-09-16).
    if (!firms.length && !vis) { setErr(t('ct_firm_required')); setSaving(false); return null; }
    // Dropping the last firm tag on a card that IS a client does not un-client it — the client
    // record stays live in the pickers, the tax scopes and the Accounting grid. Say so out loud
    // rather than let the card quietly leave the firm tabs (owner 2026-09-17, B3).
    if (!isNew && !firms.length && initFirms.length && contact?.client_id
        && !window.confirm(t('ct_untag_warn'))) { setSaving(false); return null; }
    // Required names (owner 2026-09-16): a company must have its company name; an individual must
    // have Title + First + Last — the parts documents are built from. A box holding only '-', '.' or '_'
    // counts as empty, and a company name left ending on a lone dash as incomplete (Bugify #be42fe7f).
    // Required on CREATE, and on an EDIT that changes the name or the type (Bugify #be42fe7f). An edit that
    // leaves them alone is not asked (owner 2026-09-17). Enforcing it on every edit stranded 860 of the
    // 1,121 individual cards — none of the legacy rows carry a title — so fixing a phone number demanded
    // an invented honorific, and inventing one rewrote the client's real name on save. Old ones are
    // corrected deliberately, not as a toll; such a client is asked when a document is raised for them.
    const personTouched = isNew || !opensAsPerson || namePartsKey(namePrefix, nameFirst, nameMiddle, nameLast) !== namePartsKey(p0.prefix, p0.first, p0.middle, p0.last);
    const companyTouched = isNew || opensAsPerson || name.trim() !== String(contact?.name || '').trim();
    if (entityType === 'company' && companyTouched && companyNameGap(name).length) { setErr(t('ct_need_company_name')); setSaving(false); return null; }
    if (entityType === 'individual' && personTouched && personNameGap(namePrefix, nameFirst, nameLast).length) { setErr(t('ct_need_person_name')); setSaving(false); return null; }
    // An EDIT still may not end up nameless.
    if (!isNew && entityType === 'company' && !name.trim()) { setErr(t('ct_need_company_name')); setSaving(false); return null; }
    if (!isNew && entityType === 'individual' && !nameFirst.trim() && !nameLast.trim()) { setErr(t('ct_need_person_name')); setSaving(false); return null; }
    // Firm contacts are 'public' so they appear in All + their firm tab, but NOT in
    // the generic Office tab (which filters visibility='office'). This keeps the
    // four categories mutually exclusive in the sidebar tabs.
    // Firm tag(s) drive everything: a firm contact is shared (public) and carries one or both firm
    // labels (comma-joined); a non-firm contact is Office (shared) or Private.
    const labels: string[] = [];
    if (firms.includes('legal') && firmLabel('legal')) labels.push(firmLabel('legal'));
    if (firms.includes('accounting') && firmLabel('accounting')) labels.push(firmLabel('accounting'));
    const catFields = firms.length
      ? { visibility: 'public', source_label: labels.join(',') }
      : { visibility: 'office', source_label: '' };
    // Individuals save through the four parts; empty parts keep the stored display string.
    const nameOut = entityType === 'individual' ? (assembledName || name) : name;
    const partFields = entityType === 'individual'
      ? { name_prefix: namePrefix.trim(), name_first: nameFirst.trim(), name_middle: nameMiddle.trim(), name_last: nameLast.trim() }
      : { name_prefix: '', name_first: '', name_middle: '', name_last: '' };   // a company keeps no person parts of its own (C10)
    // ⛔ ONE writable place per contact. A contact that has a client keeps using clients.active —
    // that flag gates monthly billing and every tax population, so a second copy on the contact would
    // eventually contradict it. Only a contact with NO client stores its own (owner 2026-09-19).
    const statusValue = status === 'unset' ? null : (status === 'active' ? 1 : 0);
    const linkedClientId = contact?.client_id || '';
    const payload = { name: nameOut, ...partFields, email, phone, company: entityType === 'company' ? nameOut : company, notes, entity_type: entityType, tax_id: entityType === 'company' ? taxId : '', branch_no: entityType === 'company' ? branchNo : '',
      branch_head: entityType === 'company' ? ((!branchNo || branchNo === '00000') ? 1 : 0) : 1, address, ...catFields,
      ...(linkedClientId ? {} : { active: statusValue }),
      // An existing card's is sent only when changed here, so a save never puts back a branch someone else has changed
      // since this opened.
      ...(isNew || branch !== (contact?.branch || '') ? { branch } : {}) };
    try {
      // The card is created FIRST and separately, so a later failure (marketing owners, a network
      // blip) cannot send the operator back through a POST that the name gate would now refuse
      // with a 409 naming the card they just made (owner 2026-09-17, C6).
      const row: any = createdId
        ? await apiReq('PUT', `/contacts/${createdId}`, payload)
        : isNew
          ? await apiReq('POST', '/contacts', payload)
          : await apiReq('PUT', `/contacts/${contact!.id}`, payload);
      if (isNew && row?.id && !createdId) setCreatedId(String(row.id));
      // Marketing owners write against the CLIENT the card is (or just became) — on a brand-new
      // firm card the id arrives with the save response (auto-created server-side).
      const clientId = contact?.client_id || row?.client_id;
      // Always sent, even empty — the PUT is a replace, and guarding on length made deleting the
      // last owner impossible: it came back on reopen (owner 2026-09-17, C7).
      if (clientId) await api.setMarketingOwners(clientId, mktOwners.filter(o => o.userId && o.percent > 0));
      // The status of a contact that has a client belongs to the CLIENT — that is the flag billing
      // and the tax engine read. Sent only when it was actually touched, so opening a card and
      // saving can never turn "not set" into inactive.
      if (clientId && statusValue !== null) { try { await api.updateClient(clientId, { active: statusValue }); } catch { /* the card itself is saved */ } }
      return row;
    } catch (e: any) {
      setErr(e.message);
      return null;
    } finally {
      setSaving(false);
    }
  };
  const save = async () => {
    const row = await doSave();
    if (row) { onSaved(); onClose(); }
  };
  // "Save & open client record" (new firm company): create the card, then continue straight into
  // the corporate record it just became. Closing that record closes the whole flow.
  const saveAndOpenClient = async () => {
    const row = await doSave();
    if (!row) return;
    onSaved();
    if (row.client_id) { setOpenedAfterCreate(true); setEditClientId(row.client_id); }
    else onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      onClick={handleBackdropClick}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
    >
      <div style={{
        width: 480, maxHeight: '90vh', overflowY: 'auto',
        background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl)',
        border: '1px solid var(--border)', padding: 24, boxShadow: 'var(--shadow-md)',
      }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 20 }}>
          {isNew ? t('ct_add') : t('ct_edit')}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Individual vs Company */}
          <div>
            <label style={labelStyle}>{t('ct_entity')}</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {([
                { v: 'individual', icon: 'ti-user',     label: t('ct_entity_individual') },
                { v: 'company',    icon: 'ti-building', label: t('ct_entity_company') },
              ] as { v: 'individual' | 'company'; icon: string; label: string }[]).map(({ v, icon, label }) => (
                <button key={v} type="button" onClick={() => setEntityType(v)} style={{
                  padding: '9px 8px', borderRadius: 'var(--radius-md)', border: '1px solid',
                  borderColor: entityType === v ? 'var(--accent)' : 'var(--border-strong)',
                  background: entityType === v ? 'var(--accent)' : 'transparent',
                  color: entityType === v ? '#fff' : 'var(--text-secondary)',
                  fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}>
                  <i className={`ti ${icon}`} style={{ fontSize: 14 }} aria-hidden />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Name — an individual's is structured (owner 2026-09-16); a company's IS the company. A new
              card shows neither until its type is picked (report 0d95cfed). */}
          {entityType === 'individual' ? (
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ width: 74 }}><label style={labelStyle}>{t('cl_np')}</label>
                <input value={namePrefix} onChange={e => { setNamePrefix(e.target.value); setDupWarn(''); }} onBlur={() => checkDup(assembledName)} style={inputStyle} list="ct-np-opts" placeholder="Mr." />
                <datalist id="ct-np-opts">{honorificOptions().map(o => <option key={o} value={o} />)}</datalist></div>
              {/* Capitalised as each box is left — "JOHN" / "john" → "John" — which is what the save
                  stores anyway (Calem 2026-09-25). The duplicate check ignores case. */}
              <div style={{ flex: 1 }}><label style={labelStyle}>{t('cl_nf')}</label><input value={nameFirst} onChange={e => { setNameFirst(e.target.value); setDupWarn(''); }} onBlur={() => { setNameFirst(v => normalisePersonNamePart(v)); checkDup(assembledName); }} style={inputStyle} /></div>
              <div style={{ flex: 1 }}><label style={labelStyle}>{t('cl_nm')}</label><input value={nameMiddle} onChange={e => { setNameMiddle(e.target.value); setDupWarn(''); }} onBlur={() => { setNameMiddle(v => normalisePersonNamePart(v)); checkDup(assembledName); }} style={inputStyle} /></div>
              <div style={{ flex: 1 }}><label style={labelStyle}>{t('cl_nl')}</label><input value={nameLast} onChange={e => { setNameLast(e.target.value); setDupWarn(''); }} onBlur={() => { setNameLast(v => normalisePersonNamePart(v)); checkDup(assembledName); }} style={inputStyle} /></div>
            </div>
          ) : entityType === 'company' ? (
          <div>
            <label style={labelStyle}>{t('ct_company_name')}</label>
            <input value={name} onChange={e => setNameSynced(e.target.value)} onBlur={() => checkDup(name)} style={inputStyle} placeholder="Acme Co., Ltd" />
          </div>
          ) : null}

          {dupWarn && (
            <div style={{ fontSize: 12, color: '#B45309', background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 8, padding: '7px 10px', fontWeight: 600 }}>
              <i className="ti ti-alert-triangle" style={{ marginRight: 5 }} aria-hidden />{dupWarn}
            </div>
          )}

          {/* Email */}
          <div>
            <label style={labelStyle}>{t('ct_email_addr')}</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} placeholder="jane@example.com" />
          </div>

          {/* Phone; an INDIVIDUAL may also carry the company they belong to. A company-type
              contact does not repeat itself — its Name IS the company (owner 2026-09-16). */}
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{t('ct_phone')}</label>
              <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} style={inputStyle} placeholder={phoneHint} />
            </div>
            {entityType === 'individual' && (
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{t('ct_company')}</label>
              <input value={company} onChange={e => setCompanySynced(e.target.value)} style={inputStyle} placeholder="Acme Co." />
            </div>
            )}
          </div>

          {/* Tax ID + Branch — juristic-person paperwork; an individual's form drops them
              (owner 2026-09-16). Stored values are untouched, just not edited here. */}
          {entityType === 'company' && (
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 2 }}>
              <label style={labelStyle}>{t('ct_tax_id')}</label>
              <input value={taxId} onChange={e => setTaxId(e.target.value)} style={inputStyle} placeholder="0105551234567" />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{t('ct_branch')}</label>
              <input value={branchNo} onChange={e => setBranchNo(e.target.value)} style={inputStyle} placeholder="00000" />
            </div>
          </div>
          )}

          {/* Address */}
          <div>
            <label style={labelStyle}>{t('ct_address')}</label>
            <textarea value={address} onChange={e => setAddress(e.target.value)} rows={2}
              style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }} placeholder="123 Moo 4, …" />
          </div>

          {/* Notes */}
          <div>
            <label style={labelStyle}>{t('ct_notes')}</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }}
              placeholder="Additional notes…"
            />
          </div>

          {/* Firm membership (multi — a client can be both) + visibility for non-firm contacts */}
          <div>
            <label style={labelStyle}>{t('ct_firm')}</label>
            {/* Checkboxes, not toggle pills (owner 2026-09-16): belonging is REQUIRED and starts
                unticked — a firm tick, or an explicit Office/Private below; never a silent default. */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {([
                { v: 'legal',      icon: 'ti-gavel',      label: firmLabelOr('legal', t('cs_kind_legal')),           bg: '#2B6CB0' },
                { v: 'accounting', icon: 'ti-calculator', label: firmLabelOr('accounting', t('cs_kind_accounting')), bg: '#1E9E63' },
              ] as { v: 'legal'|'accounting'; icon: string; label: string; bg: string }[]).map(({ v, icon, label, bg }) => {
                const on = firms.includes(v);
                return (
                  <label key={v} style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '9px 10px',
                    borderRadius: 'var(--radius-md)', border: '1px solid',
                    borderColor: on ? bg : 'var(--border-strong)',
                    background: on ? `${bg}14` : 'transparent',
                    color: on ? bg : 'var(--text-secondary)',
                    fontSize: 12, fontWeight: 700, cursor: 'pointer',
                  }}>
                    <input type="checkbox" checked={on} onChange={() => toggleFirm(v)} style={{ cursor: 'pointer' }} />
                    <i className={`ti ${icon}`} style={{ fontSize: 13 }} aria-hidden />
                    {label}
                  </label>
                );
              })}
            </div>
            {firms.length > 0 ? (
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 6 }}>
                <i className="ti ti-users" style={{ fontSize: 11, marginRight: 4 }} aria-hidden />{t('ct_firm_shared')}
              </div>
            ) : (
              <div style={{ marginTop: 10 }}>
                <label style={labelStyle}>{t('ct_visibility')}</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {([
                    { v: 'office',  icon: 'ti-building', label: t('ct_office'),  bg: '#6D28D9' },
                  ] as { v: 'office'; icon: string; label: string; bg: string }[]).map(({ v, icon, label, bg }) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setVis(v)}
                      style={{
                        padding: '9px 8px', borderRadius: 'var(--radius-md)',
                        border: '1px solid', borderColor: vis === v ? bg : 'var(--border-strong)',
                        background: vis === v ? bg : 'transparent', color: vis === v ? '#fff' : 'var(--text-secondary)',
                        fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                      }}
                    >
                      <i className={`ti ${icon}`} style={{ fontSize: 13 }} aria-hidden />
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Branch location (Calem, report 7be30766) — the Contacts list shows it in brackets after the name. A pill
              the size of the firm ticks above, holding a drop-down; in the accent once a branch is picked, '—' = none. */}
          <div>
            <label style={labelStyle}>{t('ct_branch_loc')}</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <label style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '0 10px',
                borderRadius: 'var(--radius-md)', border: '1px solid',
                borderColor: branch ? 'var(--accent)' : 'var(--border-strong)',
                background: branch ? 'var(--bg-active)' : 'transparent',
                color: branch ? 'var(--accent)' : 'var(--text-secondary)',
                fontSize: 12, fontWeight: 700, cursor: 'pointer',
              }}>
                <i className="ti ti-map-pin" style={{ fontSize: 13 }} aria-hidden />
                <select value={branch} onChange={e => setBranch(e.target.value)} style={{
                  flex: 1, minWidth: 0, padding: '9px 0', border: 'none', outline: 'none',
                  background: 'transparent', color: 'inherit', font: 'inherit', cursor: 'pointer',
                }}>
                  <option value="">—</option>
                  {branchOpts.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </label>
            </div>
          </div>

          {/* Marketing owners — who sourced this client. On a NEW firm card the rows are entered
              here and written right after the save creates the client (owner 2026-09-16). */}
          {firms.length > 0 && (isNew || contact?.client_id) && (
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
              <label style={labelStyle}>{t('ct_mkt_owner')}</label>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 8 }}>{t('ct_mkt_hint')}</div>
              {mktOwners.map((o, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <select value={o.userId} onChange={e => setMktOwners(prev => prev.map((x, j) => j === i ? { ...x, userId: e.target.value } : x))}
                    style={{ flex: 1, minWidth: 0, padding: '7px 9px', fontSize: 13, border: '1px solid var(--border-strong)', borderRadius: 8, background: 'var(--c-gray-50)', color: 'var(--text-primary)' }}>
                    <option value="">—</option>
                    {staffUsers.filter(u => !mktOffices.some(of => of.userId === u.id)).map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                  </select>
                  <input type="number" min={0} max={100} value={o.percent}
                    onChange={e => setMktOwners(prev => prev.map((x, j) => j === i ? { ...x, percent: Math.max(0, Math.min(100, Number(e.target.value) || 0)) } : x))}
                    style={{ width: 64, padding: '7px 9px', fontSize: 13, border: '1px solid var(--border-strong)', borderRadius: 8, background: 'var(--c-gray-50)', color: 'var(--text-primary)', textAlign: 'right' }} />
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>%</span>
                  <button type="button" onClick={() => setMktOwners(prev => prev.filter((_, j) => j !== i))} style={{ border: 'none', background: 'transparent', color: '#E24B4A', cursor: 'pointer', padding: 2 }}><i className="ti ti-x" /></button>
                </div>
              ))}
              <button type="button" onClick={() => setMktOwners(prev => [...prev, { userId: '', percent: 0 }])}
                style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--accent)', background: 'transparent', border: '1px dashed var(--accent)', borderRadius: 8, padding: '6px 11px', cursor: 'pointer' }}>
                <i className="ti ti-plus" /> {t('ct_mkt_add')}
              </button>
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 8 }}>
                {mktOffices.map(of => <div key={of.userId}>{of.email} — <b style={{ color: 'var(--text-primary)' }}>{Math.max(0, Math.round((100 - mktSum) * 100) / 100)}%</b> ({t('ct_mkt_office')})</div>)}
                {!mktOffices.length && <div><b style={{ color: 'var(--text-primary)' }}>{Math.max(0, Math.round((100 - mktSum) * 100) / 100)}%</b> ({t('ct_mkt_office')})</div>}
                {mktSum > 100 && <div style={{ color: '#E24B4A', fontWeight: 600 }}>{t('ct_mkt_over')}</div>}
              </div>
            </div>
          )}

          {/* Status. Available on EVERY card, individuals included — the owner's point on
              2026-09-19: the only switch used to live on the client record, and the button that
              reaches it is company-only, so a natural person could not be set either way. */}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
            <label style={labelStyle}>{t('ct_status')}</label>
            <div style={{ display: 'flex', gap: 6 }}>
              {([['unset', 'ct_status_unset'], ['active', 'cl_active'], ['inactive', 'cl_inactive']] as const).map(([v, k]) => (
                <button key={v} type="button" onClick={() => setStatus(v)} style={{
                  flex: 1, padding: '7px 10px', borderRadius: 8, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
                  border: `1px solid ${status === v ? 'var(--accent)' : 'var(--border-strong)'}`,
                  background: status === v ? 'var(--bg-active)' : 'transparent',
                  color: status === v ? 'var(--accent)' : 'var(--text-secondary)',
                }}>{t(k)}</button>
              ))}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 5 }}>
              {contact?.client_id ? t('ct_status_client_hint') : t('ct_status_own_hint')}
            </div>
          </div>

          {/* Client record (unified clients). Corporate machinery (tax id, registered address,
              services) — an individual's form drops it (owner 2026-09-16). On a NEW firm company
              the button saves the card and continues straight into the record it just became. */}
          {entityType === 'company' && (isNew ? firms.length > 0 : (contact!.client_id || contact!.source_label)) && (
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
              <label style={labelStyle}>{t('ct_client_record')}</label>
              <button type="button" onClick={isNew ? saveAndOpenClient : openOrCreateClient} disabled={linking || saving} style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 12px',
                borderRadius: 'var(--radius-md)', border: '1px solid #1E9E63', background: '#F4FBF7',
                color: '#1E9E63', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              }}>
                <i className={`ti ti-${!isNew && contact!.client_id ? 'user-edit' : 'user-plus'}`} style={{ fontSize: 15 }} />
                {linking || saving ? t('login_wait') : (isNew ? t('ct_save_open_client') : (contact!.client_id ? t('ct_edit_client') : t('ct_create_client')))}
              </button>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 5 }}>{t('ct_client_hint')}</div>
            </div>
          )}

          {/* Audit info */}
          {!isNew && (
            <div style={{
              fontSize: 11, color: 'var(--text-tertiary)', borderTop: '1px solid var(--border)',
              paddingTop: 10, lineHeight: 1.7,
            }}>
              <div>Created by {contact!.created_by_name || '—'} · {fmtDateTime(contact!.created_at)}</div>
              {contact!.updated_by_name && (
                <div>Updated by {contact!.updated_by_name} · {fmtDateTime(contact!.updated_at)}</div>
              )}
            </div>
          )}

          {err && (
            <div style={{
              fontSize: 12, color: '#E24B4A', background: '#FCEBEB',
              padding: '8px 12px', borderRadius: 'var(--radius-md)',
            }}>
              {err}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button onClick={onClose} style={btnStyle('var(--c-gray-100)', 'var(--text-secondary)')}>
              {t('cancel')}
            </button>
            <button onClick={save} disabled={saving} style={{ ...btnStyle('var(--accent)'), opacity: saving ? 0.7 : 1 }}>
              {saving ? t('login_wait') : t('save')}
            </button>
          </div>
        </div>
      </div>
      {editClientId !== false && (
        <EditClient clientId={editClientId} onClose={() => { setEditClientId(false); if (openedAfterCreate) onClose(); }} onSaved={onSaved} />
      )}
    </div>
  );
}

// ── Contact Card Row ─────────────────────────────────────────────────────────
interface ContactRowProps {
  contact: Contact;
  onEdit: (c: Contact) => void;
  onDelete: (c: Contact) => void;
  onMerge: (c: Contact) => void;
  onBill: (c: Contact) => void;
}

function ContactRow({ contact, onEdit, onDelete, onMerge, onBill }: ContactRowProps) {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(false);
  const vis = contact.visibility; // 'public' | 'office' | 'private'
  const configured = (contact.svc_count || 0) > 0; // service items already set up
  const { isAcct, isInactive, isMonthly, isYearly, rowBg } = rowLook(contact);
  const hasBs = isAcct && !!contact.has_bs;            // a real client of ours (has a Balance Sheet)

  // Avatar colours per visibility
  const avatarBg    = vis === 'office' ? '#EDE9FE' : vis === 'public' ? 'var(--accent-bg)' : '#E8F0FB';
  const avatarColor = vis === 'office' ? '#6D28D9'  : vis === 'public' ? 'var(--accent-text)' : '#2B6CB0';

  // Badge colours per visibility
  const badgeBg    = vis === 'office' ? '#EDE9FE' : vis === 'public' ? 'var(--accent-bg)' : 'var(--c-gray-100)';
  const badgeColor = vis === 'office' ? '#6D28D9'  : vis === 'public' ? 'var(--accent-text)' : 'var(--text-secondary)';
  const badgeLabel = vis === 'office' ? t('ct_office') : vis === 'public' ? t('ct_public') : t('ct_private');
  const badgeIcon  = vis === 'office' ? 'ti-building' : vis === 'public' ? 'ti-world' : 'ti-lock';
  // The name as the list shows it (Calem, report 101b5c69): titles and a company's legal form off, and
  // only First + Last for a person whose card holds them. The card itself keeps the full name.
  const shownName = displayClientName(contact.name, contact.name_first, contact.name_last);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 14,
        padding: '12px 16px',
        ...cardStyle({ rowBg, isInactive }, hovered),
        cursor: 'default',
      }}
    >
      {/* Avatar — its letter trimmed to its cap height, so the flex box centres the capital itself (see PlanBadges) */}
      <div style={{
        width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
        background: avatarBg, color: avatarColor,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 16, fontWeight: 700, userSelect: 'none',
      }}>
        <span style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic' }}>{getInitials(shownName)}</span>
      </div>

      {/* Main info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 3 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {shownName || <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>—</span>}
            {/* Its branch in brackets after the name, staff and clients alike (Calem, report 7be30766) */}
            {contact.branch ? ` (${contact.branch})` : null}
          </span>

          {/* Visibility badge */}
          <span style={{
            fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10,
            background: badgeBg, color: badgeColor,
            display: 'inline-flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap',
          }}>
            <i className={`ti ${badgeIcon}`} style={{ fontSize: 9 }} aria-hidden />
            {badgeLabel}
          </span>

          {/* Individual vs Company */}
          {(() => {
            const indiv = contact.entity_type === 'individual';
            return (
              <span style={{
                fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 10,
                background: indiv ? '#EFF6FF' : '#F1F5F9', color: indiv ? '#1D4ED8' : '#475569',
                display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
              }}>
                <i className={`ti ${indiv ? 'ti-user' : 'ti-building'}`} style={{ fontSize: 9 }} aria-hidden />
                {t(indiv ? 'ct_entity_individual' : 'ct_entity_company')}
              </span>
            );
          })()}

          {/* Source label badge(s) — one per firm, so a dual-firm client shows both */}
          {contact.source_label ? (
            contact.source_label.split(',').map(s => s.trim()).filter(Boolean).map(lbl => {
              const isLegal = labelKinds(lbl).legal;
              return (
                <span key={lbl} style={{
                  fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 10,
                  background: isLegal ? '#FFF7ED' : '#F0FDF4',
                  color: isLegal ? '#C2410C' : '#15803D',
                  display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
                }}>
                  <i className="ti ti-building" style={{ fontSize: 9 }} aria-hidden />
                  {lbl}
                </span>
              );
            })
          ) : contact.source === 'email' ? (
            <span style={{
              fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 10,
              background: '#EDF2FF', color: '#3B5BD5',
              display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
            }}>
              <i className="ti ti-mail" style={{ fontSize: 10 }} aria-hidden />
              {t('ct_source_email')}
            </span>
          ) : null}

          {/* Linked accounting client: type + status chips */}
          {contact.cl_type && (() => { const c = CL_TYPE_COLORS[contact.cl_type!] || CL_TYPE_COLORS.INACTIVE; return (
            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: c.bg, color: c.color, whiteSpace: 'nowrap' }}>{contact.cl_type}</span>
          ); })()}
          {parseClStatus(contact.cl_status).map(s => { const c = CL_STATUS_COLORS[s]; return c ? (
            <span key={s} style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: c.bg, color: c.color, whiteSpace: 'nowrap' }}>{t(`ac_status_${s}`)}</span>
          ) : null; })}
          {configured && (
            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: '#D1FAE5', color: '#047857', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
              <i className="ti ti-checks" style={{ fontSize: 11 }} />{contact.svc_count} {contact.svc_count === 1 ? 'service' : 'services'}
            </span>
          )}
          {hasBs && (
            <span title={t('ct_has_bs_hint')} style={{ fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 10, background: '#EEF2FF', color: '#4338CA', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
              <i className="ti ti-file-spreadsheet" style={{ fontSize: 11 }} />BS
            </span>
          )}
          {isInactive && (
            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: 'var(--c-gray-100)', color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>{t('cl_inactive')}</span>
          )}
          {isAcct && (contact.tax_services || []).map(ts => { const m = TAX_CHIP[ts]; return m ? (
            <span key={ts} title={m.title} style={{ fontSize: 9.5, fontWeight: 800, padding: '2px 6px', borderRadius: 6, background: m.bg, color: m.color, whiteSpace: 'nowrap', letterSpacing: '.02em' }}>{m.label}</span>
          ) : null; })}
        </div>

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {contact.email && (
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 4 }}>
              <i className="ti ti-mail" style={{ fontSize: 11, color: 'var(--text-tertiary)' }} aria-hidden />
              {contact.email}
            </span>
          )}
          {contact.phone && (
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 4 }}>
              <i className="ti ti-phone" style={{ fontSize: 11, color: 'var(--text-tertiary)' }} aria-hidden />
              {contact.phone}
            </span>
          )}
          {/* A person has no company line (owner 2026-09-19: "如果不是公司，就不要显示公司名").
              Individual records store their own name in `company` as well — a long-standing
              convention, 217 client rows deep — so showing it printed the person's name twice, once
              behind an office-block icon. The column is left alone; it simply is not a company. */}
          {contact.entity_type !== 'individual' && contact.company && (
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 4 }}>
              <i className="ti ti-building" style={{ fontSize: 11, color: 'var(--text-tertiary)' }} aria-hidden />
              {displayCompanyName(contact.company)}
            </span>
          )}
        </div>
      </div>

      {/* Telegram invoice binding — accounting clients only (always visible) */}
      {contact.client_id && labelKinds(contact.source_label).accounting && (
        <div style={{ flexShrink: 0 }} onClick={e => e.stopPropagation()}>
          <TelegramBind clientId={contact.client_id} iconOnly />
        </div>
      )}

      {/* M / Y bubbles — Monthly / Yearly client, at the row's right-hand end (Bugify #9ad7e6c7) */}
      <PlanBadges look={{ isMonthly, isYearly, isInactive }} />

      {/* Action buttons (visible on hover) */}
      <div style={{
        display: 'flex', gap: 4, flexShrink: 0,
        opacity: hovered ? 1 : 0, transition: 'opacity 0.15s',
      }}>
        {/* Bill this month — MONTHLY accounting clients only. The monthly run bills everyone on the 1st;
            a client added after it has no invoice, and re-running the whole month would rebuild every
            other client's draft. This bills just this one, through the same generator. */}
        {contact.client_id && isMonthly && !isInactive && (
          <button
            onClick={() => onBill(contact)}
            title={t('ct_bill_hint')}
            style={{
              width: 30, height: 30, borderRadius: 'var(--radius-md)',
              border: 'none', background: 'var(--accent-bg)',
              color: 'var(--accent-text)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <i className="ti ti-file-invoice" style={{ fontSize: 14 }} aria-hidden />
          </button>
        )}
        {contact.client_id && (
          <button
            onClick={() => window.open(`/m/client_dashboard?id=${contact.client_id}`, '_blank')}
            title="Dashboard"
            style={{
              width: 30, height: 30, borderRadius: 'var(--radius-md)',
              border: 'none', background: 'var(--bg-active)',
              color: 'var(--accent)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <i className="ti ti-layout-dashboard" style={{ fontSize: 14 }} aria-hidden />
          </button>
        )}
        <button
          onClick={() => onEdit(contact)}
          title={t('ct_edit')}
          style={{
            width: 30, height: 30, borderRadius: 'var(--radius-md)',
            border: 'none', background: 'var(--c-gray-100)',
            color: 'var(--text-secondary)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <i className="ti ti-pencil" style={{ fontSize: 14 }} aria-hidden />
        </button>
        <button
          onClick={() => onMerge(contact)}
          title={t('ct_merge_tip')}
          style={{
            width: 30, height: 30, borderRadius: 'var(--radius-md)',
            border: 'none', background: 'var(--c-gray-100)',
            color: 'var(--text-secondary)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <i className="ti ti-arrows-join-2" style={{ fontSize: 14 }} aria-hidden />
        </button>
        <button
          onClick={() => onDelete(contact)}
          title={t('ct_confirm_delete')}
          style={{
            width: 30, height: 30, borderRadius: 'var(--radius-md)',
            border: 'none', background: '#FCEBEB',
            color: '#E24B4A', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <i className="ti ti-trash" style={{ fontSize: 14 }} aria-hidden />
        </button>
      </div>
    </div>
  );
}

// ── Empty State ──────────────────────────────────────────────────────────────
function EmptyState({ label }: { label: string }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      gap: 12, padding: 60, flex: 1,
    }}>
      <div style={{
        width: 64, height: 64, borderRadius: '50%',
        background: 'var(--c-gray-100)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <i className="ti ti-address-book" style={{ fontSize: 32, color: 'var(--text-tertiary)' }} aria-hidden />
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)' }}>{label}</div>
      <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Add a contact or harvest from your email.</div>
    </div>
  );
}

// ── Tab button ───────────────────────────────────────────────────────────────
// The toolbar's All / Office / SLF Legal / SLF Accounting / External wear the top menu's Email / Contacts / Calendar
// box, as the "+" does (Calem, Bugify #667b55cb — see menuPill): the selected one its solid purple #7F77DD with white
// words, the rest its pale wash with purple words and its line. Were the app's green, and white with a grey outline.
// The rest's line is a shade darker than the box's, the page's (Calem, report 0fb815da: "Make the bubble border one
// shade darker for everything on the purple menu pages" — see pageLine).
function Tab({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '6px 14px', fontSize: 13, fontWeight: active ? 600 : 500,
        borderRadius: 'var(--radius-md)', border: '1px solid',
        ...menuPill('contact', active),
        ...(active ? {} : { borderColor: pageLine('contact') }),
        cursor: 'pointer', transition: 'all 0.1s',
      }}
    >
      {label}
    </button>
  );
}

// ── All view: the four categories side by side (Calem 2026-09-25) ───────────────────────────────
// Every column keeps its own count, scrolling and pages. Its title — or its category button in the
// toolbar — opens that category full width; 'All' brings the four columns back.
const ellipsis: React.CSSProperties = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

function MiniBtn({ icon, title, onClick, bg = 'var(--c-gray-100)', color = 'var(--text-secondary)' }: {
  icon: string; title: string; onClick: () => void; bg?: string; color?: string;
}) {
  return (
    <button onClick={e => { e.stopPropagation(); onClick(); }} title={title} style={{
      width: 24, height: 24, padding: 0, borderRadius: 'var(--radius-md)', border: 'none',
      background: bg, color, cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <i className={`ti ${icon}`} style={{ fontSize: 13 }} aria-hidden />
    </button>
  );
}

// Compact card (cardStyle): name + email. A click opens the contact; hovering shows the card's actions. A contact's
// M / Y bubbles (PlanBadges) sit at the card's right-hand end, before those actions; a long name gives way to them.
// A contact's branch follows the name in brackets (Calem, report 7be30766) and is never cut: a long name gives way to it.
function MiniRow({ name, email, branch, look, onOpen, actions }: {
  name: string; email?: string; branch?: string; look?: { rowBg: string; isInactive: boolean; isMonthly: boolean; isYearly: boolean };
  onOpen: () => void; actions: React.ReactNode;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onOpen}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', cursor: 'pointer',
        ...cardStyle(look, hovered),
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div title={branch ? `${name} (${branch})` : name} style={{ display: 'flex', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
          <span style={ellipsis}>{name || '—'}</span>
          {branch && <span style={{ flexShrink: 0, whiteSpace: 'pre' }}>{` (${branch})`}</span>}
        </div>
        <div title={email} style={{ fontSize: 11.5, color: email ? 'var(--text-secondary)' : 'var(--text-tertiary)', ...ellipsis }}>{email || '—'}</div>
      </div>
      {look && <PlanBadges look={look} />}
      {hovered && <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>{actions}</div>}
    </div>
  );
}

// Same actions as the full-width row: bill (monthly Accounting clients), edit, merge, delete.
function ContactLine({ contact, onEdit, onDelete, onMerge, onBill }: ContactRowProps) {
  const { t } = useTranslation();
  const look = rowLook(contact);
  return (
    <MiniRow name={displayClientName(contact.name, contact.name_first, contact.name_last)} email={contact.email} branch={contact.branch} look={look} onOpen={() => onEdit(contact)} actions={<>
      {contact.client_id && look.isMonthly && !look.isInactive && (
        <MiniBtn icon="ti-file-invoice" title={t('ct_bill_hint')} onClick={() => onBill(contact)} bg="var(--accent-bg)" color="var(--accent-text)" />
      )}
      <MiniBtn icon="ti-pencil" title={t('ct_edit')} onClick={() => onEdit(contact)} />
      <MiniBtn icon="ti-arrows-join-2" title={t('ct_merge_tip')} onClick={() => onMerge(contact)} />
      <MiniBtn icon="ti-trash" title={t('ct_confirm_delete')} onClick={() => onDelete(contact)} bg="#FCEBEB" color="#E24B4A" />
    </>} />
  );
}

// The page's floating "+" (Fab: 52px, 24px in from the window's bottom-right corner, Bugify #7568b3b5) sits over the
// bottom of the All view's rightmost column. That column stops this much short, so it ends 12px above the button (the
// grid's own 16px padding counted: 24 + 52 + 12 − 16) and its page buttons and last rows are never underneath it.
const FAB_ROOM = 72;

// Dressed as the top menu's Email / Contacts / Calendar box (EMAIL_CAL_GROUP) — Calem 2026-09-29 (Bugify #05f2a3d1:
// "Make each contact bubble (currently 4) look exactly the same style as the menu above for email/contacts/calendar"):
// the title bar in its wash (laid on --bg-surface, as the box is, so the very same colour) inside its purple line, with
// rounded corners; the title, count and expand icon in its purple #7F77DD, and the count chip in its wash and line, 1px
// of padding given up for the line so the chip keeps its size. Were grey lines, a --bg-panel bar, a black title and a
// grey chip and icon. Nothing frames the list under the bar: the cards float on the page — Calem, cba01676, 2nd
// opening: "Remove the borders so it looks like each bubble is floating below the menu". The whole column was a white
// box in that purple line, with a shadow, the bar its top edge. The list scrolls by mouse wheel or trackpad with no
// scrollbar showing (no-scrollbar, global.css), and neither it nor the bar keeps a strip free for one, so the cards run
// exactly the bar's width — Calem, report f6527515: "allow mouse wheel scrolling rather than a side bar … expand all
// bubbles to the full size of the relevant tab (category) above it". Both kept a scrollbar's width free down their right
// (scrollbarGutter), and a long list showed its scrollbar in that strip. The rows keep their fill (PLAIN_ROW_BG is
// already that purple: its wash on white, a touch lighter than the bar's on --bg-surface; two shades lighter before
// report 74d77066).
// The bar's line and its count chip's are a shade darker than the box's, the page's (Calem, report 0fb815da: "Make the
// bubble border one shade darker for everything on the purple menu pages" — see pageLine); the top menu's box keeps its own.
function ColumnFrame({ title, count, onExpand, bodyRef, footer, children, clearFab }: {
  title: string; count: number | null; onExpand: () => void;
  bodyRef: React.RefObject<HTMLDivElement>; footer: React.ReactNode; children: React.ReactNode;
  clearFab?: boolean;   // the column in the bottom-right corner — see FAB_ROOM
}) {
  const { t } = useTranslation();
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0,
      borderRadius: 'var(--radius-lg)', overflow: 'hidden', marginBottom: clearFab ? FAB_ROOM : 0,
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', flexShrink: 0, overflow: 'hidden' }}>
        <button onClick={onExpand} title={t('ct_col_expand')} style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px',
          border: `1px solid ${pageLine('contact')}`, borderRadius: 'var(--radius-lg)',
          backgroundColor: 'var(--bg-surface)', backgroundImage: `linear-gradient(${EMAIL_CAL_GROUP.tint}, ${EMAIL_CAL_GROUP.tint})`,
          cursor: 'pointer', textAlign: 'left',
        }}>
          <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 700, color: EMAIL_CAL_GROUP.color, ...ellipsis }}>{title}</span>
          {count !== null && (
            <span style={{ fontSize: 11, fontWeight: 700, padding: '0 7px', borderRadius: 10, border: `1px solid ${pageLine('contact')}`, background: EMAIL_CAL_GROUP.tint, color: EMAIL_CAL_GROUP.color }}>{count}</span>
          )}
          <i className="ti ti-arrows-maximize" style={{ fontSize: 14, color: EMAIL_CAL_GROUP.color }} aria-hidden />
        </button>
      </div>
      <div ref={bodyRef} className="no-scrollbar" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>{children}</div>
      {footer}
    </div>
  );
}

// First load, a failure, or nothing to show. Rows already on screen stay put while a column
// reloads, so saving a card deep down a column does not throw the reader back to the top.
function ColumnState({ err, rows }: { err: string; rows: any[] | null }) {
  const { t } = useTranslation();
  const note: React.CSSProperties = { padding: '18px 12px', textAlign: 'center', fontSize: 12, color: 'var(--text-tertiary)' };
  if (err) return <div style={{ ...note, color: '#E24B4A' }}>{err}</div>;
  if (rows === null) return <div style={note}><i className="ti ti-loader" style={{ fontSize: 20, display: 'inline-block', animation: 'spin 1s linear infinite' }} aria-hidden /></div>;
  if (!rows.length) return <div style={note}>{t('ct_col_empty')}</div>;
  return null;
}

// Office / SLF Legal / SLF Accounting: its own /contacts query, with the page's search box.
function ContactColumn({ kind, title, q, limit, reloadKey, onExpand, ...rowActions }: {
  kind: 'office' | 'legal' | 'accounting'; title: string; q: string; limit: number; reloadKey: number;
  onExpand: () => void;
} & Omit<ContactRowProps, 'contact'>) {
  const [rows, setRows] = useState<Contact[] | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [err, setErr] = useState('');
  const bodyRef = useRef<HTMLDivElement>(null);
  // A new search starts again at page 1, and every new page starts at its top.
  useEffect(() => { setPage(1); }, [q, limit]);
  useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = 0; }, [q, limit, page]);
  useEffect(() => {
    let live = true;   // an answer overtaken by a newer search is dropped
    const params = new URLSearchParams();
    if (q.trim()) params.set('q', q.trim());
    setCategoryFilter(params, kind);
    params.set('page', String(page));
    params.set('limit', String(limit));
    apiReq('GET', `/contacts?${params}`)
      .then((res: any) => { if (!live) return; setRows(res.rows || []); setTotal(res.total || 0); setErr(''); })
      .catch((e: any) => { if (live) setErr(e.message); });
    return () => { live = false; };
  }, [kind, q, limit, page, reloadKey]);

  return (
    <ColumnFrame title={title} count={total} onExpand={onExpand} bodyRef={bodyRef}
      footer={<Pagination page={page} total={total || 0} limit={limit} onPage={setPage} />}>
      <ColumnState err={err} rows={rows} />
      {rows && rows.map(c => <ContactLine key={c.id} contact={c} {...rowActions} />)}
    </ColumnFrame>
  );
}

// External pros are their own list, not /contacts, and it arrives whole — so this column applies the
// search box and the pages itself.
function ProsColumn({ title, q, limit, reloadKey, onExpand }: {
  title: string; q: string; limit: number; reloadKey: number; onExpand: () => void;
}) {
  const { t } = useTranslation();
  const [all, setAll] = useState<any[] | null>(null);
  const [page, setPage] = useState(1);
  const [err, setErr] = useState('');
  const [form, setForm] = useState<any | false>(false);
  const [bump, setBump] = useState(0);
  const bodyRef = useRef<HTMLDivElement>(null);
  useEffect(() => { setPage(1); }, [q, limit]);
  useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = 0; }, [q, limit, page]);
  useEffect(() => {
    let live = true;
    api.xproList()
      .then((d: any) => { if (live) { setAll(d.rows || []); setErr(''); } })
      .catch((e: any) => { if (live) setErr(e.message); });
    return () => { live = false; };
  }, [reloadKey, bump]);

  const s = q.trim().toLowerCase();
  const hits = all && all.filter(p => !s || [p.name, p.email, p.company, p.phone].some(v => String(v || '').toLowerCase().includes(s)));
  const rows = hits && hits.slice((page - 1) * limit, page * limit);
  const del = async (p: any) => {
    if (!window.confirm(t('ct_xpro_delete', { name: p.name }))) return;
    try { await api.xproDelete(p.id); setBump(b => b + 1); } catch (e: any) { setErr(e.message); }
  };

  return (
    <>
      <ColumnFrame title={title} count={hits ? hits.length : null} onExpand={onExpand} bodyRef={bodyRef} clearFab
        footer={<Pagination page={page} total={hits ? hits.length : 0} limit={limit} onPage={setPage} />}>
        <ColumnState err={err} rows={rows} />
        {rows && rows.map(p => (
          <MiniRow key={p.id} name={p.name} email={p.email} onOpen={() => setForm(p)} actions={<>
            <MiniBtn icon="ti-pencil" title={t('edit')} onClick={() => setForm(p)} />
            <MiniBtn icon="ti-trash" title={t('delete')} onClick={() => del(p)} bg="#FCEBEB" color="#E24B4A" />
          </>} />
        ))}
      </ColumnFrame>
      {form !== false && <ProForm pro={form} onClose={() => setForm(false)} onSaved={() => { setForm(false); setBump(b => b + 1); }} />}
    </>
  );
}

// ── Main Contact Page ────────────────────────────────────────────────────────
export default function Contact({ embedded }: { embedded?: boolean } = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(getPageSize());
  const LIMIT = pageSize;
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [tab, setTab] = useState<'all' | 'legal' | 'accounting' | 'office' | 'external'>('all');   // 'private' retired 2026-09-16

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Contact | null>(null);

  const [toast, setToast] = useState('');
  // Contact whose "Bill this month" dialog is open (preview → confirm → create).
  const [billFor, setBillFor] = useState<Contact | null>(null);
  const [mergeFrom, setMergeFrom] = useState<Contact | null>(null);
  const [harvesting, setHarvesting] = useState(false);
  // Bumped to make every column of the All view load again (after a save, delete, merge…).
  const [colsKey, setColsKey] = useState(0);

  // Load contacts (server-side search + pagination). The All view's columns load themselves.
  const load = useCallback(async (pg = page) => {
    if (tab === 'all') return;
    setLoading(true); setErr('');
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      setCategoryFilter(params, tab);
      params.set('page', String(pg));
      params.set('limit', String(LIMIT));
      const res = await apiReq('GET', `/contacts?${params}`);
      setContacts(Array.isArray(res) ? res : (res.rows || []));
      setTotal(Array.isArray(res) ? res.length : (res.total || 0));
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [q, tab, page]); // eslint-disable-line

  useEffect(() => { setPage(1); load(1); }, [q, tab, pageSize]); // eslint-disable-line
  useEffect(() => { load(page); }, [page]); // eslint-disable-line

  // Reload what is on screen: the four columns of All, or the one full-width list.
  const refresh = async (pg = page) => {
    if (tab === 'all') setColsKey(k => k + 1);
    else await load(pg);
  };

  // Delete
  const handleDelete = async (contact: Contact) => {
    if (!window.confirm(t('ct_confirm_delete'))) return;
    try {
      await apiReq('DELETE', `/contacts/${contact.id}`);
      setContacts(prev => prev.filter(c => c.id !== contact.id));
      // A dual-firm card sits in two columns, and every column's count moves.
      if (tab === 'all') setColsKey(k => k + 1);
    } catch (e: any) {
      setErr(e.message);
    }
  };

  // Harvest
  const handleHarvest = async () => {
    setHarvesting(true); setErr('');
    try {
      const res = await apiReq('POST', '/contacts/harvest');
      const n = res.added ?? res.count ?? 0;
      setToast(t('ct_harvest_done', { n }));
      setPage(1); await refresh(1);
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setHarvesting(false);
    }
  };

  // Open add/edit form
  const openAdd = () => { setEditing(null); setShowForm(true); };
  const openEdit = (c: Contact) => { setEditing(c); setShowForm(true); };

  // Deep-link: /m/contact?open=<id> (e.g. from a Job) → open that contact's form.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const openId = searchParams.get('open');
    if (!openId) return;
    apiReq('GET', `/contacts/${openId}`).then((c: any) => { setEditing(c); setShowForm(true); }).catch(() => {});
    searchParams.delete('open'); setSearchParams(searchParams, { replace: true });
  }, []); // eslint-disable-line

  // Contacts are already filtered server-side; no client-side filtering needed.
  const filtered = contacts;

  // Shared by the four columns of All. A column holds at most 100 rows a page.
  const colProps = {
    q, limit: Math.min(pageSize, 100), reloadKey: colsKey,
    onEdit: openEdit, onDelete: handleDelete, onMerge: setMergeFrom, onBill: setBillFor,
  };

  const errBanner = err && (
    <div style={{
      fontSize: 12, color: '#E24B4A', background: '#FCEBEB',
      padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8,
    }}>
      <i className="ti ti-alert-circle" style={{ fontSize: 14 }} aria-hidden />
      {err}
      <button onClick={() => setErr('')} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#E24B4A', cursor: 'pointer' }}>
        <i className="ti ti-x" style={{ fontSize: 13 }} aria-hidden />
      </button>
    </div>
  );

  // The page — round the four columns, the list's card and External's table — is white, --bg-surface (Calem, report
  // ddf1ba0e, 3rd opening: "Change every page background to white"). It was the app's beige, then for Bugify
  // #b07c8dae's 1st opening the top menu's Email / Contacts / Calendar wash one shade lighter, and for its 2nd the
  // list's grey, --c-gray-50.
  return (
    <div style={{ ...(embedded ? { flex: 1, minHeight: 0 } : { height: '100vh' }), display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)' }}>

      {/* ── Page header (hidden when embedded inside the Peak six-pack) ── */}
      {!embedded && <header style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '10px 16px', borderBottom: '1px solid var(--border)',
        background: 'var(--bg-surface)', flexShrink: 0,
      }}>
        <button
          onClick={() => navigate('/')}
          title={brandOr('Home')}
          style={{
            width: 30, height: 30, padding: 0, border: 'none',
            background: 'transparent', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <img className="app-logo" src="/logo.png" alt={brandOr('Logo')} style={{ width: 28, height: 28, objectFit: 'contain' }} />
        </button>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
          {t('mod_contact')}
        </div>
        <div style={{ flex: 1 }} />
        <button
          onClick={() => navigate('/')}
          style={{
            fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)',
            background: 'none', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 4,
          }}
        >
          <i className="ti ti-arrow-left" style={{ fontSize: 13 }} aria-hidden />
          {t('back_to_portal')}
        </button>
      </header>}

      {/* ── Toolbar ── The light grey, --c-gray-50, as File Storage's toolbar band is and every top-menu page's now
          (Calem, report 3e769579, 4th opening: "Only the top menu strip on every page turns that same grey then the
          rest of the page below this menu has a white background"). It was the page's white, --bg-surface; the page
          under it stays white. Its line is a shade darker, --border-strong where it was --border, as is the top menu's
          over it (Calem, report a903bda5, 3rd opening: "Make the gray horizontal lines one shade darker on all page menus
          like this").
          It is a bubble now, as every page toolbar is (Calem, report 76a2c198, 2nd opening — see pageToolbar): 10px
          corners and a --border-strong line all the way round where the line ran under it alone, PAGE_INSET in from
          the page's edges — the one page gutter the whole system stands in by, 15px, where this page's own was 16
          (that report's 5th opening: "Move the left and right page spacing in from the edges to 15 px on every page of
          the SLF system"; see PAGE_INSET in tabPalette) — with a gap of the page's white above and below it. The
          columns of All and the list of every other tab take the same 15 down their sides, so their ends still stand
          under the bubble's. Its grey, its tabs and its search box are as they were. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        padding: '8px 16px', ...pageToolbar(),
        background: 'var(--c-gray-50)', flexShrink: 0,
      }}>
        {/* Tabs */}
        <Tab label={t('ct_tab_all')} active={tab === 'all'} onClick={() => setTab('all')} />
        <Tab label={t('ct_tab_office')} active={tab === 'office'} onClick={() => setTab('office')} />
        <Tab label={firmLabelOr('legal', t('cs_kind_legal'))} active={tab === 'legal'} onClick={() => setTab('legal')} />
        <Tab label={firmLabelOr('accounting', t('cs_kind_accounting'))} active={tab === 'accounting'} onClick={() => setTab('accounting')} />
        <Tab label={t('ct_external')} active={tab === 'external'} onClick={() => setTab('external')} />

        <div style={{ flex: 1 }} />

        {/* Search — a round bubble in the top menu's Email / Contacts / Calendar purple, as Calendar's search box is
            (Calem, report 48c2197c, 2nd opening: "Ensure that all search boxes are the rounded bubbles and color-coded
            to that menu page"): round ends, a 1px line, 600 weight, in the box's colours from menuPill — the tabs' own,
            unselected: its pale wash, its line a shade darker (pageLine) and purple words, the placeholder's too
            (mf-ph-inherit) — with the magnifier in the purple. It has Filing's search box's size ("Do the same for every
            search bar"): 13.5px words where they were 12, 7px above and below them as before, the magnifier 14.5px
            where it was 13 and 16px in where it was 10, the words 36px in where they were 30 and 16px clear of the
            right-hand end where they were 10, 220px wide where it was 210. It reaches 1px into the toolbar's padding
            above and below, so it takes the room it took. "Search contacts…" already named the page. It was an
            8px-cornered box with the faint grey outline on the app's beige, --bg-app, its magnifier grey. */}
        <div style={{ position: 'relative', margin: '-1px 0' }}>
          <i className="ti ti-search" aria-hidden style={{
            position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
            fontSize: 14.5, color: tabColor('contact'), pointerEvents: 'none',
          }} />
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder={t('ct_search')}
            className="mf-ph-inherit"
            style={{
              width: 220, padding: '7px 16px 7px 36px', fontSize: 13.5, fontWeight: 600,
              borderRadius: 999, border: '1px solid', outline: 'none',
              ...menuPill('contact', false), borderColor: pageLine('contact'),
            }}
          />
        </div>

        {/* Harvest button */}
        <button
          onClick={handleHarvest}
          disabled={harvesting}
          style={{
            ...btnStyle('var(--c-gray-100)', 'var(--text-secondary)'),
            opacity: harvesting ? 0.7 : 1,
          }}
        >
          <i className="ti ti-mail-download" style={{ fontSize: 14 }} aria-hidden />
          {harvesting ? t('login_wait') : t('ct_harvest')}
        </button>

        {/* Refresh */}
        <button
          onClick={() => refresh(page)}
          title={t('refresh')}
          style={{
            width: 32, height: 32, borderRadius: 'var(--radius-md)',
            border: 'none', background: 'transparent',
            color: 'var(--text-secondary)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <i className="ti ti-refresh" style={{ fontSize: 15 }} aria-hidden />
        </button>
      </div>

      {/* ── All: the four categories side by side; any other tab is that category full width ── */}
      {tab === 'all' ? (<>
        {errBanner}
        <div style={{
          flex: 1, minHeight: 0, display: 'grid', gap: 12, padding: `16px ${PAGE_INSET}px`, overflowX: 'auto',
          gridTemplateColumns: 'repeat(4, minmax(220px, 1fr))', gridTemplateRows: 'minmax(0, 1fr)',
        }}>
          <ContactColumn kind="office" title={t('ct_tab_office')} onExpand={() => setTab('office')} {...colProps} />
          <ContactColumn kind="legal" title={firmLabelOr('legal', t('cs_kind_legal'))} onExpand={() => setTab('legal')} {...colProps} />
          <ContactColumn kind="accounting" title={firmLabelOr('accounting', t('cs_kind_accounting'))} onExpand={() => setTab('accounting')} {...colProps} />
          <ProsColumn title={t('ct_external')} q={q} limit={colProps.limit} reloadKey={colsKey} onExpand={() => setTab('external')} />
        </div>
      </>) : tab === 'external' ? <ExternalPros /> : (
      // Scrolls by mouse wheel or trackpad with no scrollbar showing, as the columns of All do (Calem, f6527515).
      <div className="no-scrollbar" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {errBanner}

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 48 }}>
            <i className="ti ti-loader" style={{ fontSize: 28, color: 'var(--text-tertiary)', animation: 'spin 1s linear infinite' }} aria-hidden />
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            <EmptyState label={t('ct_empty')} />
          </div>
        ) : (
          // Unframed, as the columns of All are (cba01676): the cards float on the page, as wide as the heading
          // above them. The list was a white box in a grey line, with a shadow.
          <div style={{ margin: `16px ${PAGE_INSET}px`, borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
            {/* List header */}
            <div style={{
              display: 'flex', alignItems: 'center',
              padding: '8px 16px 8px 70px',
              borderBottom: '1px solid var(--border-strong)',
              background: 'var(--bg-panel)',
            }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {total} contact{total !== 1 ? 's' : ''}
              </span>
            </div>
            {filtered.map(c => (
              <ContactRow
                key={c.id}
                contact={c}
                onEdit={openEdit}
                onDelete={handleDelete}
                onMerge={setMergeFrom}
                onBill={setBillFor}
              />
            ))}
            <Pagination page={page} total={total} limit={LIMIT} onPage={p => setPage(p)} onLimit={n => { setPageSize(n); setPage(1); }} />
          </div>
        )}
      </div>)}

      {/* Add — the shared floating "+", in the top menu's Email / Contacts / Calendar purple. Calem 2026-09-28 (Bugify
          #7568b3b5, 2nd opening: "Remove the add contact button from the menu and replace it with the plus symbol in the
          bottom corner") — it takes the place of the toolbar's "+ Add Contact" button. */}
      <Fab onClick={openAdd} title={t('ct_add')} color={tabColor('contact')} />

      {/* ── Modal ── */}
      {showForm && (
        <ContactForm
          contact={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => { refresh(page); }}
        />
      )}

      {/* ── Toast ── */}
      {billFor && (
        <BillMonthDialog
          clientId={billFor.client_id!}
          clientName={billFor.company || billFor.name}
          onClose={() => setBillFor(null)}
          onDone={() => refresh()}
        />
      )}
      {mergeFrom && (
        <MergeContactDialog
          keep={mergeFrom}
          onClose={() => setMergeFrom(null)}
          onDone={(msg) => { setMergeFrom(null); setToast(msg); refresh(); }}
        />
      )}
      {toast && <Toast message={toast} onDismiss={() => setToast('')} />}
    </div>
  );
}

// ── Merge two cards for the same person (owner 2026-09-19) ──────────────────────────────────────
// The card the operator clicked is the one that SURVIVES; they search for the duplicate to absorb.
// Nothing happens without a preview: the server decides whether the two may be merged at all (a
// person and a company never are, nor two different tax ids or two different client files) and says
// what the surviving card will look like afterwards.
function MergeContactDialog({ keep, onClose, onDone }: { keep: Contact; onClose: () => void; onDone: (msg: string) => void }) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<any[]>([]);
  const [pick, setPick] = useState<any | null>(null);
  const [preview, setPreview] = useState<any | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const s = q.trim();
    if (s.length < 2) { setHits([]); return; }
    let live = true;
    const h = setTimeout(async () => {
      try { const r: any = await api.searchContacts(s); if (live) setHits((r.rows || r || []).filter((x: any) => x.id !== keep.id).slice(0, 8)); }
      catch { /* typing */ }
    }, 220);
    return () => { live = false; clearTimeout(h); };
  }, [q, keep.id]);

  const choose = async (c: any) => {
    setPick(c); setPreview(null); setErr('');
    try { setPreview(await apiReq('GET', `/contacts/${keep.id}/merge-preview?loser=${encodeURIComponent(c.id)}`)); }
    catch (e: any) { setErr(e.message); }
  };
  const go = async () => {
    if (!pick || !preview || busy) return;
    setBusy(true);
    try { await apiReq('POST', `/contacts/${keep.id}/merge`, { loserId: pick.id }); onDone(t('ct_merge_done', { name: keep.name })); }
    catch (e: any) { setErr(e.message); setBusy(false); }
  };

  const box: React.CSSProperties = { border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', fontSize: 12.5 };
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 11000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg-surface)', borderRadius: 14, width: 'min(96vw, 560px)', maxHeight: '88vh', overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>{t('ct_merge_title')}</div>
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 3 }}>{t('ct_merge_sub')}</div>
        </div>

        <div style={{ ...box, background: 'var(--bg-active)', borderColor: 'var(--accent)' }}>
          <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.04em', color: 'var(--accent)' }}>{t('ct_merge_keep')}</div>
          <div style={{ fontWeight: 700, color: 'var(--text-primary)', marginTop: 2 }}>{keep.name}</div>
          <div style={{ color: 'var(--text-secondary)' }}>{keep.email || '—'}</div>
        </div>

        <div>
          <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 5 }}>{t('ct_merge_pick')}</label>
          <input value={q} onChange={e => { setQ(e.target.value); setPick(null); setPreview(null); setErr(''); }} placeholder={t('ct_merge_search')} autoFocus
            style={{ width: '100%', padding: '9px 11px', fontSize: 13, border: '1px solid var(--border-strong)', borderRadius: 8, background: 'var(--bg-app)', color: 'var(--text-primary)', outline: 'none' }} />
          {!!hits.length && !pick && (
            <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {hits.map(h => (
                <button key={h.id} type="button" onClick={() => choose(h)} style={{ ...box, textAlign: 'left', cursor: 'pointer', background: 'transparent', color: 'var(--text-primary)' }}>
                  <b>{h.name}</b>{h.company ? <span style={{ color: 'var(--text-tertiary)' }}> · {h.company}</span> : null}
                  <div style={{ color: 'var(--text-tertiary)' }}>{h.email || '—'}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        {pick && (
          <div style={{ ...box, background: '#FEF6F5', borderColor: '#F0C2BD' }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.04em', color: '#B4524A' }}>{t('ct_merge_absorb')}</div>
            <div style={{ fontWeight: 700, color: 'var(--text-primary)', marginTop: 2 }}>{pick.name}</div>
            <div style={{ color: 'var(--text-secondary)' }}>{pick.email || '—'}</div>
          </div>
        )}

        {!!err && <div style={{ fontSize: 12.5, color: '#B42318', background: '#FCEBEB', padding: '9px 12px', borderRadius: 8, lineHeight: 1.5 }}>{err}</div>}

        {preview && !err && (
          <div style={{ ...box }}>
            <div style={{ fontWeight: 800, marginBottom: 6, color: 'var(--text-primary)' }}>{t('ct_merge_after')}</div>
            <div style={{ color: 'var(--text-secondary)' }}>{t('ct_merge_email')}: <b style={{ color: 'var(--text-primary)' }}>{preview.emailAfter || '—'}</b></div>
            {(preview.fills || []).map((f: any) => (
              <div key={f.field} style={{ color: 'var(--text-secondary)' }}>{f.field}: <b style={{ color: 'var(--text-primary)' }}>{f.value}</b> <span style={{ color: 'var(--text-tertiary)' }}>({t('ct_merge_filled')})</span></div>
            ))}
            {!preview.fills?.length && <div style={{ color: 'var(--text-tertiary)' }}>{t('ct_merge_nofill')}</div>}
            <div style={{ color: 'var(--text-tertiary)', marginTop: 6 }}>{t('ct_merge_soft')}</div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{t('cancel')}</button>
          <button onClick={go} disabled={!preview || !!err || busy} style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: (!preview || !!err || busy) ? 'var(--c-gray-200)' : 'var(--accent)', color: (!preview || !!err || busy) ? 'var(--text-tertiary)' : '#fff', fontSize: 13, fontWeight: 800, cursor: (!preview || !!err || busy) ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {busy && <i className="ti ti-loader" style={{ fontSize: 13, display: 'inline-block', animation: 'spin 1s linear infinite' }} />}
            {t('ct_merge_go')}
          </button>
        </div>
      </div>
    </div>
  );
}
